import React, { useCallback } from 'react';
import styled, { css } from 'styled-components';
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete';
import { TextInput } from '../Input';
import { Card } from '../Card';
import useMenu from '../../hooks/useMenu';
import { usePlacesAutocompleteField } from './usePlacesAutocompleteField';

const Wrapper = styled.div`
  position: relative;
`;

const Options = styled(Card)`
  z-index: 6;
  ${({ innerStyle }) =>
    css`
      ${innerStyle}
    `}
`;

const Option = styled.div`
  padding: 15px;
  cursor: pointer;

  &:hover {
    background: ${({ theme }) => theme.colors.white};
  }
`;

const PlacesAutocomplete = ({
  title,
  value: selectedValue,
  onChange,
  disabled,
  clearable = false,
  onClear,
  usePortal,
  className,
  required,
  error,
  portalStyle,
  ...restProps
}) => {
  const {
    ready,
    value: placesAutocompleteValue,
    suggestions: { status, data },
    setValue: setPlacesAutocompleteValue,
    clearSuggestions
  } = usePlacesAutocomplete({
    debounce: 300
  });

  const { Portal, wrapperRef, style, menuRef, isOpen, setOpen, setClose } = useMenu({
    usePortal,
    onClose: clearSuggestions
  });

  const { value, onChangeValue } = usePlacesAutocompleteField({
    selectedValue,
    placesAutocompleteValue,
    setPlacesAutocompleteValue,
    clearSuggestions,
    setOpen,
    setClose
  });

  const handleClear = useCallback(() => {
    onChangeValue('', false);
    onChange?.('', {});
    onClear?.();
  }, [onChange, onChangeValue, onClear]);

  const handleSelect = useCallback(
    ({ description }) => async () => {
      onChangeValue(description, false);
      clearSuggestions();
      setClose();

      try {
        const results = await getGeocode({ address: description });
        const { lat, lng } = await getLatLng(results[0]);
        onChange(description, { lat, lng });
      } catch (err) {
        // error
      }
    },
    [clearSuggestions, onChange, onChangeValue, setClose]
  );

  return (
    <Wrapper
      ref={(node) => {
        wrapperRef.current = node;
      }}
      className={className}>
      <TextInput
        title={title}
        value={value}
        onChange={onChangeValue}
        disabled={!ready || disabled}
        required={required}
        error={error}
        clearable={clearable}
        onClear={handleClear}
        {...restProps}
      />
      {isOpen && status === 'OK' && data?.length > 0 && (
        <Portal>
          <Options ref={menuRef} innerStyle={{ ...style, ...(portalStyle || {}) }}>
            {data.map((suggestion, index) => {
              const {
                structured_formatting: { main_text: main, secondary_text: secondary }
              } = suggestion;

              return (
                <Option key={index} onClick={handleSelect(suggestion)}>
                  <strong>{main}</strong> <small>{secondary}</small>
                </Option>
              );
            })}
          </Options>
        </Portal>
      )}
    </Wrapper>
  );
};

export default PlacesAutocomplete;
